import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import dayjs, { type Dayjs } from 'dayjs'
import { ChevronLeft, ChevronRight, Loader } from 'lucide-react'
import { Button } from '@/components/shadcn/button'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/shadcn/popover'
import { cn } from '@/lib/utils'
import { buildMonthGrid, dayKey } from './monthGrid'

/** An event to render in a day cell. */
export interface DayEvent {
  key: string
  label: string
  color: string
  /** Click on the event (e.g. edit). Only editable events set this. */
  onClick?: () => void
}

interface Props {
  month: Dayjs
  eventsByDay: Map<string, DayEvent[]>
  isLoading?: boolean
  onPrev: () => void
  onNext: () => void
  onToday: () => void
  /** Click on a day cell — e.g. create an appointment on that day. */
  onSelectDay?: (day: Dayjs) => void
}

const MAX_VISIBLE = 3


function EventRow({ e }: { e: DayEvent }) {
  return (
    <div
      key={e.key}
      title={e.label}
      onClick={e.onClick ? (ev) => {
        ev.stopPropagation()
        e.onClick!()
      } : undefined}
      className={cn(
        'flex items-center gap-1 truncate rounded px-1 py-0.5 text-[11px] leading-tight',
        e.onClick && 'cursor-pointer hover:brightness-95',
      )}
      style={{ backgroundColor: `${e.color}1a` }}
    >
      <span className="size-1.5 shrink-0 rounded-full" style={{ backgroundColor: e.color }} />
      <span className="truncate">{e.label}</span>
    </div>
  )
}

/** Pure month calendar (display): header with navigation + Monday-based grid. */
export function MonthCalendar({ month, eventsByDay, isLoading, onPrev, onNext, onToday, onSelectDay }: Props) {
  const { t } = useTranslation(['service-modules-calendar', 'common'])
  const monthLabels = t('dates.months', { ns: 'common', returnObjects: true }) as string[]
  const weekdayLabels = t('dates.weekdaysShort', { ns: 'common', returnObjects: true }) as string[]
  const { days } = useMemo(() => buildMonthGrid(month), [month])
  const today = dayjs().startOf('day')

  return (
    <div className="flex flex-col rounded-xl border bg-card">
      {/* Header */}
      <div className="flex items-center justify-between gap-2 border-b p-3">
        <div className="flex items-center gap-2">
          <span className="text-base font-semibold">
            {monthLabels[month.month()]} {month.year()}
          </span>
          {isLoading && <Loader className="size-4 animate-spin text-primary" />}
        </div>
        <div className="flex items-center gap-1">
          <Button variant="outline" size="sm" onClick={onToday}>
            {t('calendar.today')}
          </Button>
          <Button variant="outline" size="icon" className="size-8" onClick={onPrev}
                  aria-label={t('calendar.prevMonth')}>
            <ChevronLeft className="size-4" />
          </Button>
          <Button variant="outline" size="icon" className="size-8" onClick={onNext}
                  aria-label={t('calendar.nextMonth')}>
            <ChevronRight className="size-4" />
          </Button>
        </div>
      </div>

      {/* Weekdays */}
      <div className="grid grid-cols-7 border-b text-xs font-medium text-muted-foreground">
        {weekdayLabels.map((w) => (
          <div key={w} className="px-2 py-1.5 text-center">
            {w}
          </div>
        ))}
      </div>

      {/* Days */}
      <div className="grid grid-cols-7">
        {days.map((day) => {
          const inMonth = day.month() === month.month()
          const isToday = day.isSame(today, 'day')
          const events = eventsByDay.get(dayKey(day)) ?? []
          const overflow = events.length - MAX_VISIBLE

          return (
            <div
              key={dayKey(day)}
              onClick={onSelectDay ? () => onSelectDay(day) : undefined}
              className={cn(
                'min-h-24 border-b border-r p-1.5 last:border-r-0 [&:nth-child(7n)]:border-r-0',
                !inMonth && 'bg-muted/30',
                onSelectDay && 'cursor-pointer hover:bg-muted/40',
              )}
            >
              <div
                className={cn(
                  'mb-1 flex size-6 items-center justify-center rounded-full text-xs',
                  inMonth ? 'text-foreground' : 'text-muted-foreground',
                  isToday && 'bg-primary font-semibold text-primary-foreground',
                )}
              >
                {day.date()}
              </div>

              <div className="flex flex-col gap-0.5">
                {events.slice(0, MAX_VISIBLE).map((e) => (
                  <EventRow key={e.key} e={e} />
                ))}

                {overflow > 0 && (
                  <Popover>
                    <PopoverTrigger asChild>
                      <button
                        onClick={(ev) => ev.stopPropagation()}
                        className="px-1 text-left text-[11px] text-muted-foreground hover:text-foreground"
                      >
                        {t('calendar.more', { count: overflow })}
                      </button>
                    </PopoverTrigger>
                    <PopoverContent
                      className="w-52 p-2"
                      onClick={(ev) => ev.stopPropagation()}
                    >
                      <p className="mb-1.5 text-xs font-medium text-muted-foreground">
                        {day.format('DD.MM.YYYY')}
                      </p>
                      <div className="flex flex-col gap-0.5">
                        {events.slice(MAX_VISIBLE).map((e) => (
                          <EventRow key={e.key} e={e} />
                        ))}
                      </div>
                    </PopoverContent>
                  </Popover>
                )}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
